import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getTeacherDashboard, getCourseAnalytics } from '../../services/analytics';
import api from '../../services/api';
import {
    TrendingUp, BookOpen, Users, BarChart3, Star, Clock,
    ChevronDown, AlertTriangle, CheckCircle, Target, Award,
    PieChart, Activity, Search, ListChecks
} from 'lucide-react';

/* ─── Simple Bar Chart (SVG) ─── */
function BarChart({ data, labelKey, valueKey, color = '#3B82F6', height = 200 }) {
    if (!data?.length) return <p className="text-sm text-gray-400 text-center py-8">Ma'lumot yo'q</p>;
    const max = Math.max(...data.map(d => d[valueKey]), 1);
    const barWidth = Math.min(40, Math.floor(600 / data.length) - 8);

    return (
        <div className="overflow-x-auto">
            <svg width={Math.max(data.length * (barWidth + 8) + 40, 300)} height={height + 40} className="mx-auto">
                {data.map((d, i) => {
                    const barH = (d[valueKey] / max) * height;
                    const x = i * (barWidth + 8) + 20;
                    return (
                        <g key={i}>
                            <rect x={x} y={height - barH} width={barWidth} height={barH} rx={4} fill={color} opacity={0.85} />
                            <text x={x + barWidth / 2} y={height - barH - 6} textAnchor="middle" className="text-[10px] fill-gray-600 dark:fill-gray-400">{d[valueKey]}</text>
                            <text x={x + barWidth / 2} y={height + 16} textAnchor="middle" className="text-[9px] fill-gray-400">{d[labelKey]?.slice(-5)}</text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}

/* ─── Donut Chart (SVG) ─── */
function DonutChart({ value, max = 100, color = '#3B82F6', size = 100, label }) {
    const pct = max > 0 ? Math.min(value / max, 1) : 0;
    const r = 38;
    const circ = 2 * Math.PI * r;
    return (
        <div className="flex flex-col items-center">
            <svg width={size} height={size} viewBox="0 0 100 100">
                <circle cx="50" cy="50" r={r} fill="none" stroke="#E5E7EB" strokeWidth="8" className="dark:stroke-gray-700" />
                <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="8"
                    strokeDasharray={`${circ * pct} ${circ * (1 - pct)}`}
                    strokeDashoffset={circ * 0.25} strokeLinecap="round" />
                <text x="50" y="50" textAnchor="middle" dominantBaseline="central" className="text-lg font-bold fill-gray-900 dark:fill-gray-100">
                    {Math.round(pct * 100)}%
                </text>
            </svg>
            {label && <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">{label}</span>}
        </div>
    );
}

/* ─── Stat Mini Card ─── */
function Stat({ icon, label, value, color, sub }) {
    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2 mb-2">
                <div className={`w-8 h-8 rounded-lg bg-${color}-100 dark:bg-${color}-900/30 text-${color}-600 dark:text-${color}-300 flex items-center justify-center`}>{icon}</div>
                <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
            </div>
            <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{value}</div>
            {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
        </div>
    );
}

/* ─── Per-student work table ─── */
function StudentWorkTable({ students }) {
    const [query, setQuery] = useState('');
    const [sortKey, setSortKey] = useState('tests_done');

    const rows = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = (students || []).filter(s =>
            !q || s.name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q)
        );
        return [...list].sort((a, b) => {
            if (sortKey === 'name') return (a.name || '').localeCompare(b.name || '');
            return (b[sortKey] ?? -1) - (a[sortKey] ?? -1);
        });
    }, [students, query, sortKey]);

    if (!students?.length) {
        return <p className="text-sm text-gray-400 text-center py-6">Bu kursga hali talaba yozilmagan</p>;
    }

    const worked = students.filter(s => s.tests_done > 0 || s.lessons_completed > 0).length;
    const scoreColor = (v) => v == null ? 'text-gray-400' : v >= 80 ? 'text-green-600 dark:text-green-400' : v >= 60 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400';
    const fmtDate = (d) => d ? new Date(d.replace(' ', 'T')).toLocaleDateString('uz-UZ', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

    return (
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <ListChecks className="w-4 h-4 text-blue-500" /> Talabalar bo'yicha bajarilgan ishlar
                    <span className="text-xs font-normal text-gray-400">({worked} / {students.length} ta talaba ishlagan)</span>
                </h3>
                <div className="flex gap-2">
                    <div className="relative">
                        <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Qidirish..."
                            className="pl-8 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 outline-none focus:border-blue-500 w-40 sm:w-52" />
                    </div>
                    <select value={sortKey} onChange={e => setSortKey(e.target.value)}
                        className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 outline-none focus:border-blue-500">
                        <option value="tests_done">Testlar soni</option>
                        <option value="lessons_completed">Darslar soni</option>
                        <option value="attempts">Urinishlar</option>
                        <option value="avg_score">O'rtacha ball</option>
                        <option value="name">Ism (A-Z)</option>
                    </select>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-600">
                            <th className="py-2 pr-3 font-medium">#</th>
                            <th className="py-2 pr-3 font-medium">Talaba</th>
                            <th className="py-2 px-3 font-medium text-center">Ishlagan testlar</th>
                            <th className="py-2 px-3 font-medium text-center">Urinishlar</th>
                            <th className="py-2 px-3 font-medium text-center">Tugatgan darslar</th>
                            <th className="py-2 px-3 font-medium text-center">O'rtacha ball</th>
                            <th className="py-2 pl-3 font-medium text-right">Oxirgi test</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((s, i) => (
                            <tr key={s.id} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                                <td className="py-2.5 pr-3 text-gray-400">{i + 1}</td>
                                <td className="py-2.5 pr-3">
                                    <div className="font-medium text-gray-900 dark:text-gray-100">{s.name}</div>
                                    <div className="text-xs text-gray-400">{s.email}</div>
                                </td>
                                <td className="py-2.5 px-3 text-center">
                                    <span className="font-bold text-gray-900 dark:text-gray-100">{s.tests_done}</span>
                                    <span className="text-xs text-gray-400"> / {s.total_tests}</span>
                                </td>
                                <td className="py-2.5 px-3 text-center text-gray-700 dark:text-gray-300">{s.attempts}</td>
                                <td className="py-2.5 px-3 text-center">
                                    <span className="font-bold text-gray-900 dark:text-gray-100">{s.lessons_completed}</span>
                                    <span className="text-xs text-gray-400"> / {s.total_lessons}</span>
                                </td>
                                <td className={`py-2.5 px-3 text-center font-semibold ${scoreColor(s.avg_score)}`}>
                                    {s.avg_score != null ? `${Math.round(s.avg_score)}%` : '—'}
                                </td>
                                <td className="py-2.5 pl-3 text-right text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtDate(s.last_activity)}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={7} className="py-6 text-center text-gray-400">Hech narsa topilmadi</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default function TeacherAnalytics() {
    const [dashboard, setDashboard] = useState(null);
    const [courses, setCourses] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [courseData, setCourseData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [courseLoading, setCourseLoading] = useState(false);

    useEffect(() => {
        Promise.all([
            getTeacherDashboard(),
            api.get('/courses'),
        ]).then(([dash, coursesRes]) => {
            setDashboard(dash.data);
            const list = coursesRes.data?.data || coursesRes.data || [];
            setCourses(list);
            if (list.length > 0) loadCourseAnalytics(list[0].id);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const loadCourseAnalytics = async (courseId) => {
        setSelectedCourseId(courseId);
        setCourseLoading(true);
        try {
            const r = await getCourseAnalytics(courseId);
            setCourseData(r.data);
        } catch { setCourseData(null); }
        finally { setCourseLoading(false); }
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" /></div>;

    const selectedCourse = courses.find(c => c.id === selectedCourseId);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl p-8 text-white">
                <h1 className="text-2xl font-bold mb-2 flex items-center gap-2"><TrendingUp className="w-6 h-6" /> Analitika</h1>
                <p className="text-indigo-200">Kurslaringiz bo'yicha batafsil statistika</p>
            </div>

            {/* Overview Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat icon={<BookOpen className="w-4 h-4" />} label="Jami kurslar" value={dashboard?.total_courses || 0} color="blue" />
                <Stat icon={<Users className="w-4 h-4" />} label="Jami talabalar" value={dashboard?.total_students || 0} color="green" />
                <Stat icon={<BarChart3 className="w-4 h-4" />} label="Jami yozilishlar" value={dashboard?.total_enrollments || 0} color="purple" />
                <Stat icon={<Target className="w-4 h-4" />} label="O'rtacha tugatish" value={`${dashboard?.avg_completion_rate || 0}%`} color="orange" />
            </div>

            {/* Course-level analytics */}
            {courses.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                    {/* Course selector */}
                    <div className="p-5 border-b border-gray-100 dark:border-gray-700">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kursni tanlang</label>
                        <div className="relative">
                            <select value={selectedCourseId || ''} onChange={e => loadCourseAnalytics(Number(e.target.value))}
                                className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl outline-none focus:border-blue-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 appearance-none pr-10">
                                {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                            </select>
                            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none" />
                        </div>
                    </div>

                    {courseLoading ? (
                        <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent" /></div>
                    ) : courseData ? (
                        <div className="p-5 space-y-6">
                            {/* Course overview row */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                                    <Users className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                                    <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{courseData.total_students || 0}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Talabalar</div>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                                    <Target className="w-5 h-5 text-green-500 mx-auto mb-1" />
                                    <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{courseData.completion_rate || 0}%</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Tugatish foizi</div>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                                    <Star className="w-5 h-5 text-yellow-500 mx-auto mb-1" />
                                    <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{courseData.avg_rating ? Number(courseData.avg_rating).toFixed(1) : '—'}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">O'rtacha baho ({courseData.reviews_count || 0})</div>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                                    <Award className="w-5 h-5 text-purple-500 mx-auto mb-1" />
                                    <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{courseData.test_stats?.avg_score ? Math.round(courseData.test_stats.avg_score) + '%' : '—'}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">O'rtacha test bali</div>
                                </div>
                            </div>

                            {/* Per-student work */}
                            <StudentWorkTable students={courseData.student_stats} />

                            {/* Charts row */}
                            <div className="grid lg:grid-cols-2 gap-6">
                                {/* Completion donut */}
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
                                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2"><PieChart className="w-4 h-4" /> Tugatish ko'rsatkichi</h3>
                                    <div className="flex items-center justify-center gap-8">
                                        <DonutChart value={courseData.completion_rate || 0} color="#22C55E" label="Tugatish" />
                                        <div className="space-y-2 text-sm">
                                            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-green-500" /><span className="text-gray-600 dark:text-gray-400">Tugatgan</span></div>
                                            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-gray-300 dark:bg-gray-600" /><span className="text-gray-600 dark:text-gray-400">Davom etmoqda</span></div>
                                        </div>
                                    </div>
                                </div>

                                {/* Enrollment trend */}
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
                                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2"><Activity className="w-4 h-4" /> Yozilish dinamikasi (30 kun)</h3>
                                    <BarChart data={courseData.enrollment_trend || []} labelKey="date" valueKey="count" color="#6366F1" height={140} />
                                </div>
                            </div>

                            {/* Test statistics */}
                            {courseData.test_stats && (
                                <div className="space-y-4">
                                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Test statistikasi</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Jami urinishlar</div>
                                            <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{courseData.test_stats.total_attempts || 0}</div>
                                        </div>
                                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">O'rtacha ball</div>
                                            <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{courseData.test_stats.avg_score ? Math.round(courseData.test_stats.avg_score) + '%' : '—'}</div>
                                        </div>
                                    </div>

                                    {/* Hardest questions */}
                                    {courseData.test_stats.hardest_questions?.length > 0 && (
                                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
                                            <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Eng qiyin savollar</h4>
                                            <div className="space-y-2">
                                                {courseData.test_stats.hardest_questions.map((q, i) => (
                                                    <div key={i} className="flex items-start gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-600">
                                                        <span className="text-xs font-bold text-red-500 bg-red-50 dark:bg-red-900/30 px-2 py-1 rounded flex-shrink-0">{Math.round(q.correct_rate)}%</span>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm text-gray-900 dark:text-gray-100 line-clamp-2">{q.text}</p>
                                                            <p className="text-xs text-gray-400 mt-0.5">{q.total_attempts} urinish</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Score distribution */}
                                    {courseData.test_stats.score_distribution?.length > 0 && (
                                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
                                            <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-blue-500" /> Baho taqsimoti</h4>
                                            <BarChart data={courseData.test_stats.score_distribution} labelKey="range" valueKey="count" color="#8B5CF6" height={120} />
                                        </div>
                                    )}

                                    {/* Average time per test */}
                                    {courseData.test_stats.per_test?.length > 0 && (
                                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
                                            <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-cyan-500" /> Testlar bo'yicha</h4>
                                            <div className="space-y-2">
                                                {courseData.test_stats.per_test.map((t, i) => (
                                                    <div key={i} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-600">
                                                        <span className="text-sm text-gray-900 dark:text-gray-100 font-medium truncate flex-1">{t.title}</span>
                                                        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                                                            <span>O'rtacha: <b className="text-gray-900 dark:text-gray-100">{Math.round(t.avg_score)}%</b></span>
                                                            <span>Vaqt: <b className="text-gray-900 dark:text-gray-100">{t.avg_time ? Math.round(t.avg_time / 60) + ' daq' : '—'}</b></span>
                                                            <span>{t.attempts} urinish</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="p-12 text-center text-gray-400">Ma'lumot yuklanmadi</div>
                    )}
                </div>
            )}

            {courses.length === 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center border border-gray-100 dark:border-gray-700">
                    <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Hali kurslaringiz yo'q</h3>
                    <Link to="/teacher/courses" className="text-blue-600 text-sm font-medium">Kurs yaratish →</Link>
                </div>
            )}
        </div>
    );
}
